// Utils
import { errorLogger } from "@Common/helpers/logger";

// Constants
const DEFAULT_DECIMAL_PLACES = 3;

export type DealShape = { amountFinanced: number; age: number; dealStatus?: string; balance?: number };
type TGenerateAnalyticsDataArgs = {
	deals: DealShape[];
	graphWidth: number;
	decimalPlaces?: number;
	propToUse?: keyof Omit<DealShape, "age">;
};

function calculateAmountsByStatus(
	deals: DealShape[],
	propToUse: keyof Omit<DealShape, "age">
): Record<"delivered" | "closed" | "finalized" | "total", number> {
	const deliveredDeals = deals.filter((entry) => entry.dealStatus === "E");
	const closedDeals = deals.filter((entry) => entry.dealStatus === "C");
	const finalizedDeals = deals.filter((entry) => entry.dealStatus === "F");

	// Use count by default (for dealStatus prop)
	let deliveredAmount = deliveredDeals.length;
	let closedAmount = closedDeals.length;
	let finalizedAmount = finalizedDeals.length;

	// Override with actual amounts for numeric properties
	if (propToUse === "balance") {
		deliveredAmount = deliveredDeals.reduce((acc, entry) => acc + (entry?.balance ?? 0), 0);
		closedAmount = closedDeals.reduce((acc, entry) => acc + (entry?.balance ?? 0), 0);
		finalizedAmount = finalizedDeals.reduce((acc, entry) => acc + (entry?.balance ?? 0), 0);
	} else if (propToUse === "amountFinanced") {
		deliveredAmount = deliveredDeals.reduce((acc, entry) => acc + entry.amountFinanced, 0);
		closedAmount = closedDeals.reduce((acc, entry) => acc + entry.amountFinanced, 0);
		finalizedAmount = finalizedDeals.reduce((acc, entry) => acc + entry.amountFinanced, 0);
	}

	return {
		delivered: deliveredAmount,
		closed: closedAmount,
		finalized: finalizedAmount,
		total: deliveredAmount + closedAmount + finalizedAmount,
	};
}

export function generateAnalyticsData({
	deals,
	graphWidth,
	decimalPlaces = DEFAULT_DECIMAL_PLACES,
	propToUse = "balance",
}: TGenerateAnalyticsDataArgs) {
	try {
		const intervalsCount = 9; // including the final step
		const newIntervals = Array.from({ length: intervalsCount }, (_, i) => ({
			from: i * graphWidth,
			to: (i + 1) * graphWidth,
		}));

		return newIntervals.map((interval) => {
			const dealsInInterval = deals.filter((entry) => {
				if (interval.from === 0 && entry.age <= 0) {
					return true;
				}
				return interval.to !== intervalsCount * graphWidth
					? entry.age >= interval.from && entry.age < interval.to
					: entry.age >= interval.from;
			});

			const totalBalance = dealsInInterval.reduce((acc, entry) => acc + Number(entry[propToUse]), 0);
			const amounts = calculateAmountsByStatus(dealsInInterval, propToUse);

			return {
				age: interval.to !== intervalsCount * graphWidth ? interval.to.toString() : "\ua74e",
				quantity: dealsInInterval.length,
				amount: (totalBalance / 1000000).toFixed(decimalPlaces),
				exactAmount: totalBalance,
				...amounts,
			};
		});
	} catch (error) {
		errorLogger(error as Error);
		return [];
	}
}
